import type { GenreConfig } from './types';

export const POP: GenreConfig = {
  id: 'pop',
  siteName: 'PopAlbumReviews',
  domain: 'popalbumreviews.com',
  noun: 'pop',
  label: 'Pop',
  shortName: 'PAR',
  tagline: 'In-Depth Pop Album Reviews',
  description:
    'In-depth pop album reviews with a published scorecard: songwriting, production, performance and cohesion, from classic pop and synth-pop to modern art pop, K-pop and streaming-era records.',
  readerLine: 'a serious pop listener',
  xHandle: null,
  gaId: 'G-DF6SCPBPBC',
  ahrefsKey: 'V9wq8N6JY2eM9nHVxuXNsQ',
  clarityId: 'z38qccdnmb',
  accentHue: 320,

  dailyReviews: 1,
  launchReviews: 20,
  trackLimits: { min: 5, max: 30 },

  musicbrainzTags: ['pop', 'synth-pop', 'dance-pop', 'electropop', 'art pop', 'indie pop', 'pop rock', 'teen pop', 'k-pop'],
  wikipediaArtistSuffixes: ['(singer)', '(musician)', '(band)', '(group)'],
  wikipediaArtistSearchTerm: 'singer',
  artistKeywordSuffix: 'singer',

  scorecard: {
    dimensions: [
      {
        key: 'writing',
        label: 'Songwriting',
        weight: 0.25,
        measures:
          'Hooks, melody, chorus craft and lyrics: whether the songs are built to last, with choruses that earn their lift instead of leaning on repetition.',
      },
      {
        key: 'production',
        label: 'Production',
        weight: 0.25,
        measures:
          'Arrangement, sound design and mix: how well the record is built and how well the sound serves the songs.',
      },
      {
        key: 'delivery',
        label: 'Performance',
        weight: 0.15,
        measures:
          'Vocals, character, phrasing and features: the performance itself, from the control of the lead vocal to the chemistry of the guests.',
      },
      {
        key: 'cohesion',
        label: 'Cohesion',
        weight: 0.15,
        measures:
          'Sequencing, pacing, length and filler: whether it works as an album rather than a playlist of singles.',
      },
      {
        key: 'originality',
        label: 'Originality',
        weight: 0.1,
        measures:
          "What it adds, measured against the artist's own catalog and the peers of its moment, not against what came later.",
      },
      {
        key: 'replay',
        label: 'Replay value',
        weight: 0.1,
        measures:
          'How it holds up on repeat listens and over time. For recent releases this is a projection and the note says so.',
      },
    ],
    topBandMeaning: 'Among the best pop albums ever made on this measure.',
  },

  canonicalGenres: [
    'Pop',
    'Dance-Pop',
    'Synth-Pop',
    'Electropop',
    'Art Pop',
    'Indie Pop',
    'Pop Rock',
    'Teen Pop',
    'R&B Pop',
    'K-Pop',
    'Hyperpop',
    'Chamber Pop',
  ],

  primaryGenres: [
    { label: 'Pop', slug: 'pop', hue: 320, styles: ['classic-pop', 'adult-contemporary', 'power-pop', 'sophisti-pop'] },
    { label: 'Dance-Pop', slug: 'dance-pop', hue: 300, styles: ['disco-pop', 'eurodance', 'club-pop', 'house-pop'] },
    { label: 'Synth-Pop', slug: 'synth-pop', hue: 200, styles: ['new-wave', 'new-romantic', 'futurepop', 'dark-synth-pop'] },
    { label: 'Electropop', slug: 'electropop', hue: 280, styles: ['electroclash', 'synthwave-pop', 'nu-disco', 'bedroom-pop'] },
    { label: 'Art Pop', slug: 'art-pop', hue: 260, styles: ['avant-pop', 'baroque-pop', 'experimental-pop', 'glam-pop'] },
    { label: 'Indie Pop', slug: 'indie-pop', hue: 160, styles: ['dream-pop', 'twee-pop', 'jangle-pop', 'indietronica'] },
    { label: 'Pop Rock', slug: 'pop-rock', hue: 20, styles: ['soft-rock', 'heartland-rock', 'pop-punk', 'britpop'] },
    { label: 'Teen Pop', slug: 'teen-pop', hue: 340, styles: ['bubblegum-pop', 'boy-band', 'girl-group', 'disney-pop'] },
    { label: 'R&B Pop', slug: 'rnb-pop', hue: 30, styles: ['contemporary-r-and-b', 'new-jack-swing', 'blue-eyed-soul', 'neo-soul'] },
    { label: 'K-Pop', slug: 'k-pop', hue: 190, styles: ['idol-pop', 'j-pop', 'latin-pop', 'europop'] },
    { label: 'Hyperpop', slug: 'hyperpop', hue: 120, styles: ['pc-music', 'glitchcore', 'digicore'] },
    { label: 'Chamber Pop', slug: 'chamber-pop', hue: 60, styles: ['orchestral-pop', 'singer-songwriter-pop', 'folk-pop'] },
  ],

  genreDescriptions: {
    pop:
      'The broad center of popular music: songs built around melody, a clear verse and chorus, and mass appeal. Modern pop took shape from the 1950s and 1960s with the Beatles, Motown and the Brill Building writers, and has absorbed nearly every style since. Everything else on this site is a branch or a neighbour of it.',
    'dance-pop':
      'Pop built for the dance floor: steady four-on-the-floor or syncopated rhythms, bright hooks and polished production. Dance-pop grew out of disco and 1980s club music with Madonna and Janet Jackson, and was carried through the 1990s and 2000s by Britney Spears, Kylie Minogue and Max Martin-era production.',
    'synth-pop':
      'Pop where synthesizers and drum machines lead. Synth-pop emerged in the late 1970s and early 1980s with Kraftwerk as a reference point and with Depeche Mode, Pet Shop Boys, Human League and Eurythmics defining its sound.',
    electropop:
      'Pop that puts electronic production at the front, often with processed vocals and club-leaning rhythms. Electropop grew from synth-pop and became a mainstream sound in the 2000s and 2010s through artists such as Robyn, Lady Gaga and Purity Ring.',
    'art-pop':
      'Pop that treats the album and the persona as artistic statements while keeping the song form. Art pop draws on the glam and art-school lineage of David Bowie and Roxy Music and runs through Kate Bush, Prince, Bjork and Lorde to modern auteurs.',
    'indie-pop':
      'Pop made outside the major-label system, often guitar-led or home-recorded, with a focus on melody and intimacy. Indie pop took shape in 1980s Britain around labels like Creation and Sarah and has since broadened into dream pop and bedroom pop.',
    'pop-rock':
      'Pop songwriting with a band-led rock arrangement. Pop rock grew from the British Invasion and 1970s soft rock, and spans Fleetwood Mac, Tom Petty, Bon Jovi, Oasis and Taylor Swift crossover eras, including pop-punk offshoots.',
    'teen-pop':
      'Pop aimed at a young audience, with bright hooks, polished production and a strong sense of image. Teen pop runs from the 1960s bubblegum era through New Kids on the Block and the Backstreet Boys and N Sync boom of the late 1990s, to Disney-era stars and One Direction.',
    'rnb-pop':
      'Pop that blends contemporary R&B rhythm and vocal technique with pop structure. It took shape with Michael Jackson, Whitney Houston and Janet Jackson, and has been a mainstream force through Mariah Carey, Beyonce, Rihanna and The Weeknd.',
    'k-pop':
      'South Korean pop built on the idol system, with precisely produced songs, genre switches inside a single track and elaborate choreography and visuals. K-pop took its modern form in the 1990s with Seo Taiji and Boys and H.O.T., and became a global force in the 2010s and 2020s with BTS, Blackpink and others.',
    hyperpop:
      'Pop pushed to excess: pitched vocals, distorted synths, fast tempos and a deliberately artificial finish. Hyperpop grew from the PC Music collective in the 2010s and artists like SOPHIE, 100 gecs and Charli XCX.',
    'chamber-pop':
      'Pop arranged with strings, brass, piano and other orchestral colour, usually with a focus on songwriting and intimacy. Chamber pop draws on Burt Bacharach and Brian Wilson and carries through Scott Walker, Belle and Sebastian, Sufjan Stevens and Lana Del Rey.',
  },

  genreGuides: {
    pop: {
      sound:
        'Pop albums are built on melody, a strong chorus and a voice that can carry it, wrapped in production that sounds of its moment. The form is flexible: ballads, dance tracks and guitar songs all sit under the same umbrella. This tag is the broad one every album on the site can fall under; the narrower genre pages split it by style and era.',
      listenFor:
        'Our scores weight songwriting and production most heavily, at 25% each, because a pop record lives or dies on its hooks and on whether the sound serves them. Albums with a few great singles and a lot of filler land in the middle bands; albums with great songs and a generic mix do too.',
    },
    'dance-pop': {
      sound:
        'Dance-pop is built on rhythm first: a groove that holds across the track, a hook that lands on the beat and a vocal that rides the production rather than sitting on top. Tempos tend to cluster between 100 and 130 BPM, and the best records vary texture inside that range.',
      listenFor:
        'The scorecard asks whether the groove serves the song or replaces it. Production and performance matter more here than in a ballad album, but a record of twelve similar tempos loses points on cohesion and replay.',
    },
    'synth-pop': {
      sound:
        'Synth-pop uses synthesizers, sequencers and drum machines as the lead instruments, often with cool, direct vocals. Sound design is part of the songwriting: a patch can be as memorable as a melody.',
      listenFor:
        'Production is judged for how the electronics build mood and space, not for gloss alone. Albums where the machines serve strong melodies score highest; those that rely on era-specific sounds without songs behind them lose points on songwriting and replay.',
    },
    'art-pop': {
      sound:
        'Art pop keeps the song but treats the album as a designed object: unusual arrangements, a defined persona and concepts that run across tracks. It is often more demanding than mainstream pop and rewards repeat listening.',
      listenFor:
        'Ambition does not score on its own. The scorecard asks whether the strangeness is earned by the songs, whether the concept holds across the sequence, and whether the record offers something its peers did not. Originality is weighted at 10%, but a record without hooks will lose more on songwriting.',
    },
    'k-pop': {
      sound:
        'K-pop songs are produced to stack ideas: a verse, a pre-chorus, a rap break, a dance break and a chorus can sit in a three-minute track. Records are often released as EPs or mini albums, with visual concepts that frame the music.',
      listenFor:
        'The scorecard rewards songs that survive without the choreography and videos. Cohesion is judged on the sequence as a record, not on its singles, and production and performance are weighed on how well the group voices are used.',
    },
  },

  styleExamples: [
    'dance-pop', 'synth-pop', 'electropop', 'art pop', 'indie pop', 'dream pop',
    'power pop', 'sophisti-pop', 'baroque pop', 'hyperpop', 'k-pop', 'new wave',
  ],
  shortGenreSuffixes: [' Pop'],

  prompt: {
    evidenceExamples: 'a hook, a chorus lift, a vocal take, a production choice, a sequencing decision',
    fillerTracks: 'intros, interludes, remixes and bonus tracks',
    analysisFocus: 'songwriting, production and performance. Name producers, songwriters and engineers only from the FACT SHEET.',
    primaryKeywordExample: '"Michael Jackson Thriller review" or "Robyn Body Talk review"',
    secondaryKeywordExample: '"Thriller album analysis", "best synth-pop albums", "Michael Jackson sixth album"',
    headingExamples:
      '"Michael Jackson Thriller — Pop Builds a Whole Album of Singles", "Robyn Body Talk — Where Dance-Pop Learned to Cry"',
  },

  voices: [
    {
      name: 'Mara',
      title: 'Lineage Voice',
      initials: 'M',
      bio: 'The house voice for pop history: where an album sits in the lineage from the Brill Building and Motown onward, who it learned from and who learned from it.',
      specialization: 'Lineage',
      featured: true,
      avatarHue: 320,
      lens: `LENS: Lineage
Place the album in pop history: what it answered, which artists it learned from, which artists learned from it.
Voice: direct, confident, willing to disagree with the consensus.`,
      match: {
        artists: ['the beatles', 'the supremes', 'the jackson 5', 'abba', 'carole king', 'burt bacharach', 'the carpenters', 'the bee gees', 'fleetwood mac', 'elton john', 'stevie wonder', 'billy joel', 'carly simon', 'olivia newton-john', 'neil diamond', 'cher', 'dionne warwick', 'dusty springfield', 'diana ross', 'tina turner'],
        labels: ['motown', 'columbia', 'atlantic', 'rca'],
        keywords: ['classic', 'brill building', 'motown', 'sixties', 'seventies', 'legacy'],
      },
    },
    {
      name: 'Dani',
      title: 'Dance & Club Voice',
      initials: 'D',
      bio: 'Listens from the dance floor: disco, house, dance-pop and club culture, and how rhythm and tempo shape a pop record.',
      specialization: 'Dance',
      featured: true,
      avatarHue: 300,
      lens: `LENS: The dance floor
Hear the album through club music: groove, tempo, build and release, the DJ edit. Ask whether the rhythm carries the songs or replaces them, and whether the record works front to back.
Voice: physical and exact about how the music moves.`,
      match: {
        artists: ['madonna', 'donna summer', 'kylie minogue', 'janet jackson', 'dua lipa', 'robyn', 'cher', 'daft punk', 'calvin harris', 'jessie ware', 'róisín murphy', 'roisin murphy', 'carly rae jepsen', 'britney spears', 'beyoncé', 'beyonce', 'sophie ellis-bextor', 'lady gaga'],
        labels: ['sire', 'casablanca', 'parlophone', 'polydor', 'ministry of sound'],
        keywords: ['dance', 'disco', 'club', 'house', 'groove', 'remix', 'nu-disco'],
      },
    },
    {
      name: 'Nico',
      title: 'Production & Sound Design Voice',
      initials: 'N',
      bio: 'Listens production-first: arrangement, synth patches, vocal processing and the mix, and the producers who shaped how pop sounds.',
      specialization: 'Production',
      featured: true,
      avatarHue: 190,
      lens: `LENS: Production first
Listen to the album as a recording: arrangement, sound design, vocal processing, the low end, compression and space. Treat producers and engineers named in the FACT SHEET as co-authors.
Voice: precise and practical, technical without jargon for its own sake.`,
      match: {
        artists: ['max martin', 'quincy jones', 'timbaland', 'pharrell williams', 'the neptunes', 'jack antonoff', 'dr. luke', 'stargate', 'a. g. cook', 'a.g. cook', 'sophie', 'arca', 'finneas', 'jon brion', 'mark ronson', 'nile rodgers', 'trevor horn', 'brian eno', 'giorgio moroder', 'pet shop boys'],
        labels: ['xl recordings', 'zttt', 'pc music', 'interscope'],
        keywords: ['production', 'producer', 'remaster', 'mix', 'sound design', 'synth', 'vocal processing'],
      },
    },
    {
      name: 'Theo',
      title: 'Songwriting & Hooks Voice',
      initials: 'T',
      bio: 'Reads the songwriting closely: melody, harmony, hooks and chorus craft, and whether the lyrics earn their place.',
      specialization: 'Songwriting',
      featured: true,
      avatarHue: 20,
      lens: `LENS: The songwriting
Study how the songs are built: melody, chord movement, hook placement, verse-to-chorus lift and lyric craft. Explain the mechanics of what works and what doesn't.
Voice: incisive and analytical, never decorative.`,
      match: {
        artists: ['taylor swift', 'carole king', 'elton john', 'paul mccartney', 'the beatles', 'billy joel', 'carly simon', 'sara bareilles', 'ed sheeran', 'adele', 'harry styles', 'olivia rodrigo', 'sabrina carpenter', 'charli xcx', 'carly rae jepsen', 'abba', 'max martin', 'katy perry'],
        labels: ['republic', 'big machine', 'columbia', 'island'],
        keywords: ['songwriting', 'hook', 'chorus', 'melody', 'lyrics', 'songwriter'],
      },
    },
    {
      name: 'Odile',
      title: 'Art Pop & Auteurs Voice',
      initials: 'O',
      bio: 'Covers pop as an art form: auteurs, concept albums and personas, and the artists who turned the album into a designed object.',
      specialization: 'Art pop',
      featured: false,
      avatarHue: 260,
      lens: `LENS: Art pop and auteurs
Hear the album as a designed statement: persona, concept, visual world and how the songs serve them. Ask whether the ambition is earned by the music.
Voice: curious and exact; never confuse strangeness with depth.`,
      match: {
        artists: ['david bowie', 'kate bush', 'prince', 'björk', 'bjork', 'roxy music', 'grace jones', 'talking heads', 'lorde', 'fka twigs', 'st. vincent', 'lana del rey', 'fiona apple', 'annie lennox', 'bryan ferry', 'rosalía', 'rosalia', 'frank ocean', 'janelle monáe', 'janelle monae'],
        labels: ['4ad', 'warp', 'one little independent', 'young'],
        keywords: ['art pop', 'concept', 'avant', 'experimental', 'persona', 'auteur', 'baroque'],
      },
    },
    {
      name: 'Joon',
      title: 'Global Pop Voice',
      initials: 'J',
      bio: 'Follows pop beyond the US and UK: K-pop, J-pop, Latin pop and European pop, and what each scene added.',
      specialization: 'Global',
      featured: false,
      avatarHue: 170,
      lens: `LENS: Global pop
Position the album in the wider pop world: South Korea, Japan, Latin America, Scandinavia, continental Europe. Notice what is local and what travels, and which industry model shaped the record.
Voice: comparative and border-crossing.`,
      match: {
        artists: ['bts', 'blackpink', 'twice', 'newjeans', 'stray kids', 'exo', 'girls\' generation', 'bigbang', 'shakira', 'bad bunny', 'rosalía', 'rosalia', 'ricky martin', 'j balvin', 'abba', 'roxette', 'ace of base', 'utada hikaru', 'perfume', 'kyary pamyu pamyu'],
        labels: ['sm entertainment', 'jyp', 'hybe', 'yg entertainment', 'sony latin', 'universal latin'],
        keywords: ['k-pop', 'j-pop', 'latin', 'korean', 'japanese', 'swedish', 'scandinavian', 'spanish', 'europop', 'idol'],
      },
    },
    {
      name: 'Zoe',
      title: 'Teen Pop & Stardom Voice',
      initials: 'Z',
      bio: 'Covers teen pop, boy bands, girl groups and the fandoms around them, and how image and audience shape a record.',
      specialization: 'Stardom',
      featured: false,
      avatarHue: 340,
      lens: `LENS: Teen pop and stardom
Hear the album as made for an audience: fandom, image, the star-making machinery. Judge the songs on their own terms, not on the seriousness of the genre's critics.
Voice: warm, unembarrassed and exact about craft.`,
      match: {
        artists: ['britney spears', 'backstreet boys', '*nsync', 'nsync', 'new kids on the block', 'spice girls', 'destiny\'s child', 'christina aguilera', 'justin bieber', 'one direction', 'miley cyrus', 'selena gomez', 'ariana grande', 'jonas brothers', 'take that', 'hanson', 'the monkees', 'kelly clarkson', 'olivia rodrigo'],
        labels: ['jive', 'hollywood records', 'rca', 'syco'],
        keywords: ['teen', 'boy band', 'girl group', 'fandom', 'idol', 'disney', 'bubblegum'],
      },
    },
    {
      name: 'Remy',
      title: 'Streaming Era Voice',
      initials: 'R',
      bio: 'Covers pop since 2012: streaming, playlists, viral hits and the albums built for a changed industry, and is the voice most likely to push back on the canon.',
      specialization: 'Streaming era',
      featured: false,
      avatarHue: 140,
      lens: `LENS: The streaming era
Test the canon instead of deferring to it. Understand how streaming, playlists, short-form video and shrinking album cycles changed pop; judge new sounds on their own terms and say plainly when a record is built for the playlist.
Voice: sharp, contrarian when the evidence supports it.`,
      match: {
        artists: ['billie eilish', 'dua lipa', 'olivia rodrigo', 'sabrina carpenter', 'chappell roan', 'charli xcx', 'the weeknd', 'doja cat', 'tate mcrae', 'ariana grande', 'harry styles', 'lizzo', 'sza', 'lorde', 'troye sivan', 'gracie abrams', 'lana del rey', '100 gecs', 'addison rae', 'taylor swift'],
        labels: ['republic', 'xo', 'columbia', 'interscope', 'atlantic'],
        keywords: ['streaming', 'tiktok', 'viral', 'playlist', 'surprise album', 'deluxe'],
        yearAfter: 2012,
      },
    },
    {
      name: 'Pia',
      title: 'Industry & Rollout Voice',
      initials: 'P',
      bio: 'Reads the business around the record: labels, singles strategy, rollouts, chart campaigns and how the release shaped the music.',
      specialization: 'Industry',
      featured: false,
      avatarHue: 40,
      lens: `LENS: Industry and rollout
Read the album as a product and a campaign: singles, release strategy, label politics, chart performance, and how those pressures show in the sequence and sound. Use only facts from the FACT SHEET for sales and chart claims.
Voice: contextual and practical, never cynical for its own sake.`,
      match: {
        artists: ['michael jackson', 'madonna', 'whitney houston', 'mariah carey', 'celine dion', 'britney spears', 'beyoncé', 'beyonce', 'taylor swift', 'rihanna', 'katy perry', 'lady gaga', 'justin timberlake', 'adele', 'ed sheeran', 'bruno mars', 'the weeknd', 'spice girls', 'backstreet boys', 'janet jackson'],
        labels: ['epic', 'arista', 'sony', 'universal', 'warner', 'def jam', 'island def jam'],
        keywords: ['chart', 'single', 'rollout', 'label', 'blockbuster', 'comeback', 'bestselling', 'multi-platinum'],
      },
    },
    {
      name: 'Sasha',
      title: 'Identity & Social Context Voice',
      initials: 'S',
      bio: 'Reads albums for what they say about identity and the world around them: gender, sexuality, race, class, and how pop carries those conversations.',
      specialization: 'Identity',
      featured: false,
      avatarHue: 110,
      lens: `LENS: Identity and social context
Read the album as a statement from a particular place and time: who it speaks as and to, which conversations it joins, and whether the words match the weight of the music. Paraphrase; never quote lyrics.
Voice: contextual and accessible, never academic for its own sake.`,
      match: {
        artists: ['madonna', 'lady gaga', 'janelle monáe', 'janelle monae', 'beyoncé', 'beyonce', 'lizzo', 'sam smith', 'troye sivan', 'chappell roan', 'frank ocean', 'lil nas x', 'kesha', 'pink', 'p!nk', 'christina aguilera', 'alanis morissette', 'tracy chapman', 'sinéad o\'connor', 'sinead o\'connor', 'taylor swift'],
        labels: ['motown', 'parkwood', 'rca', 'epic'],
        keywords: ['identity', 'feminist', 'queer', 'lgbtq', 'protest', 'empowerment', 'race', 'coming of age', 'political'],
      },
    },
  ],

  gapTemplates: {
    bestOfYear: 'Best Pop Albums of {year}',
    era: 'Pop in the {era}',
    subgenresExplained: 'Pop Subgenres Explained',
  },
};
